import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { LineChart } from "@mui/x-charts";
import Grid from "@mui/material/Grid2";
import api from "services/api";
import {
  EmptyNote,
  Panel,
  SegmentedControl,
  StatTile,
  Toolbar,
  formatPercent,
} from "scenes/server/serverUi";

/**
 * Server history (2026-10-21).
 *
 * Renders the downsampled series from /server/history. Bucketing happens on
 * the server (1 / 5 / 15 minute buckets by range), so a 7-day view never
 * ships thousands of points to the browser.
 */

const RANGES = [
  { label: "1h", hours: 1 },
  { label: "6h", hours: 6 },
  { label: "24h", hours: 24 },
  { label: "7d", hours: 168 },
];

const formatTick = (isoValue, hours) => {
  const date = new Date(isoValue);
  if (hours > 48) {
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

const ChartCard = ({ title, subtitle, children }) => (
  <Panel title={title} subtitle={subtitle}>
    {children}
  </Panel>
);

const ServerHistory = () => {
  const theme = useTheme();

  const [hours, setHours] = useState(24);
  const [series, setSeries] = useState([]);
  const [bucketMinutes, setBucketMinutes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (rangeHours) => {
    setLoading(true);
    try {
      const response = await api.get("/server/history", {
        params: { hours: rangeHours },
      });
      setSeries(Array.isArray(response.data?.series) ? response.data.series : []);
      setBucketMinutes(response.data?.bucketMinutes ?? null);
      setError("");
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error || "Failed to load server history."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(hours);
  }, [hours, load]);

  const xAxisData = useMemo(
    () => series.map((point) => point.t),
    [series]
  );

  const peak = useMemo(() => {
    const values = (key) =>
      series.map((point) => point[key]).filter((value) => value !== null);

    const max = (list) => (list.length === 0 ? null : Math.max(...list));
    const avg = (list) =>
      list.length === 0
        ? null
        : Math.round((list.reduce((sum, v) => sum + v, 0) / list.length) * 10) / 10;
    const last = (list) => (list.length === 0 ? null : list[list.length - 1]);

    return {
      cpu: {
        max: max(values("cpu")),
        avg: avg(values("cpu")),
        current: last(values("cpu")),
      },
      memory: {
        max: max(values("memory")),
        avg: avg(values("memory")),
        current: last(values("memory")),
      },
      disk: {
        max: max(values("disk")),
        avg: avg(values("disk")),
        current: last(values("disk")),
      },
    };
  }, [series]);

  const chartHeight = 220;

  const sharedXAxis = [
    {
      data: xAxisData,
      scaleType: "point",
      tickLabelStyle: { fontSize: 11 },
      valueFormatter: (value) => formatTick(value, hours),
    },
  ];

  if (loading && series.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Range + summary. Previously a ToggleButtonGroup sitting flush under
          the tab bar with 8px of padding, so it read as part of the tabs. */}
      <Toolbar>
        <SegmentedControl
          ariaLabel="History range"
          value={hours}
          onChange={setHours}
          options={RANGES.map((range) => ({
            value: range.hours,
            label: range.label,
          }))}
        />

        <Typography variant="caption" color="text.secondary">
          {series.length} point{series.length === 1 ? "" : "s"}
          {bucketMinutes ? ` · ${bucketMinutes}-minute buckets` : ""}
        </Typography>

        {series.length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: { xs: 2, sm: 3 },
              ml: "auto",
              flexWrap: "wrap",
            }}
          >
            <StatTile
              label="CPU now"
              value={formatPercent(peak.cpu.current)}
              hint={`avg ${peak.cpu.avg ?? "—"}% · peak ${peak.cpu.max ?? "—"}%`}
            />
            <StatTile
              label="Memory now"
              value={formatPercent(peak.memory.current)}
              hint={`avg ${peak.memory.avg ?? "—"}% · peak ${peak.memory.max ?? "—"}%`}
            />
            <StatTile
              label="Disk now"
              value={formatPercent(peak.disk.current)}
              hint={`avg ${peak.disk.avg ?? "—"}% · peak ${peak.disk.max ?? "—"}%`}
            />
          </Box>
        )}
      </Toolbar>

      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}

      {series.length === 0 && !error && (
        <Panel>
          <EmptyNote>
            No snapshots in this range yet. The collector writes one row per
            minute — check that the cron job is installed if this stays empty.
          </EmptyNote>
        </Panel>
      )}

      {series.length > 0 && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <ChartCard
              title="CPU"
              subtitle={
                peak.cpu.max === null
                  ? "No samples"
                  : `avg ${peak.cpu.avg}% · peak ${peak.cpu.max}%`
              }
            >
              <LineChart
                height={chartHeight}
                xAxis={sharedXAxis}
                yAxis={[{ min: 0, max: 100, label: "%" }]}
                series={[
                  {
                    data: series.map((point) => point.cpu),
                    label: "CPU %",
                    color: theme.palette.primary.main,
                    showMark: false,
                    curve: "natural",
                    connectNulls: true,
                    valueFormatter: (value) =>
                      value === null ? "N/A" : `${Number(value).toFixed(1)}%`,
                  },
                ]}
                margin={{ left: 50, right: 16, top: 16, bottom: 32 }}
                grid={{ horizontal: true }}
                sx={{
                  "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": {
                    stroke: theme.palette.divider,
                  },
                }}
              />
            </ChartCard>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <ChartCard
              title="Memory"
              subtitle={
                peak.memory.max === null
                  ? "No samples"
                  : `avg ${peak.memory.avg}% · peak ${peak.memory.max}%`
              }
            >
              <LineChart
                height={chartHeight}
                xAxis={sharedXAxis}
                yAxis={[{ min: 0, max: 100, label: "%" }]}
                series={[
                  {
                    data: series.map((point) => point.memory),
                    label: "Memory %",
                    color: theme.palette.warning.main,
                    showMark: false,
                    curve: "natural",
                    connectNulls: true,
                    valueFormatter: (value) =>
                      value === null ? "N/A" : `${Number(value).toFixed(1)}%`,
                  },
                ]}
                margin={{ left: 50, right: 16, top: 16, bottom: 32 }}
                grid={{ horizontal: true }}
              />
            </ChartCard>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <ChartCard
              title="Disk"
              subtitle={
                peak.disk.max === null
                  ? "No samples"
                  : `avg ${peak.disk.avg}% · peak ${peak.disk.max}%`
              }
            >
              <LineChart
                height={chartHeight}
                xAxis={sharedXAxis}
                yAxis={[{ min: 0, max: 100, label: "%" }]}
                series={[
                  {
                    data: series.map((point) => point.disk),
                    label: "Disk %",
                    color: theme.palette.error.main,
                    showMark: false,
                    curve: "natural",
                    connectNulls: true,
                    valueFormatter: (value) =>
                      value === null ? "N/A" : `${Number(value).toFixed(1)}%`,
                  },
                ]}
                margin={{ left: 50, right: 16, top: 16, bottom: 32 }}
                grid={{ horizontal: true }}
              />
            </ChartCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ServerHistory;
